/**
 * Club Reservations - Manage courts and bookings for club owners
 * Note: Playtomic integration is disabled/coming soon
 */
import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Calendar, Clock, Plus, Edit2, Trash2, ArrowLeft, X,
  Check, AlertCircle, Play, QrCode, Copy, Settings,
  ChevronLeft, ChevronRight, Users, DollarSign, Search, Zap
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import api from '../../services/api';
import { toast } from 'sonner';

// Court modal
const CourtModal = ({ court, onClose, onSave }) => {
  const [formData, setFormData] = useState(court || {
    name: '',
    court_type: 'padel',
    description: '',
    price_per_hour: 300,
    has_hiksports: false,
    features: [],
    is_active: true,
  });
  const [featureInput, setFeatureInput] = useState('');
  const [saving, setSaving] = useState(false);

  const handleAddFeature = () => {
    if (featureInput.trim()) {
      setFormData({
        ...formData,
        features: [...(formData.features || []), featureInput.trim()]
      });
      setFeatureInput('');
    }
  };

  const handleRemoveFeature = (idx) => {
    setFormData({
      ...formData,
      features: formData.features.filter((_, i) => i !== idx)
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.price_per_hour) {
      toast.error('Completa los campos requeridos');
      return;
    }

    setSaving(true);
    try {
      await onSave(formData);
      onClose();
    } finally {
      setSaving(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-background/80 backdrop-blur-sm"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-lg bg-card rounded-2xl border border-border shadow-xl max-h-[90vh] overflow-y-auto"
      >
        <div className="sticky top-0 bg-card border-b border-border p-4 flex items-center justify-between">
          <h2 className="font-display font-bold text-lg">
            {court ? 'Editar Cancha' : 'Nueva Cancha'}
          </h2>
          <Button variant="ghost" size="icon" onClick={onClose}>
            <X className="w-5 h-5" />
          </Button>
        </div>

        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          <div>
            <Label>Nombre *</Label>
            <Input
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="Ej: Cancha 1 - Central"
              required
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Tipo</Label>
              <select
                value={formData.court_type}
                onChange={(e) => setFormData({ ...formData, court_type: e.target.value })}
                className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
              >
                <option value="padel">Pádel</option>
                <option value="tennis">Tenis</option>
                <option value="futbol">Fútbol</option>
                <option value="basket">Básquetbol</option>
                <option value="other">Otro</option>
              </select>
            </div>
            <div>
              <Label>Precio por hora *</Label>
              <Input
                type="number"
                min="0"
                value={formData.price_per_hour}
                onChange={(e) => setFormData({ ...formData, price_per_hour: parseFloat(e.target.value) || 0 })}
                required
              />
            </div>
          </div>

          <div>
            <Label>Descripción</Label>
            <Input
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Descripción opcional"
            />
          </div>

          <div className="flex items-center gap-3 p-3 rounded-lg bg-primary/10 border border-primary/30">
            <input
              type="checkbox"
              checked={formData.has_hiksports}
              onChange={(e) => setFormData({ ...formData, has_hiksports: e.target.checked })}
              className="w-5 h-5 rounded"
            />
            <div>
              <p className="font-medium flex items-center gap-2">
                <Play className="w-4 h-4 text-primary" />
                Sistema Clipper Replay
              </p>
              <p className="text-xs text-muted-foreground">
                Esta cancha tiene cámara y botón de replay
              </p>
            </div>
          </div>

          <div>
            <Label>Características</Label>
            <div className="flex gap-2 mb-2">
              <Input
                value={featureInput}
                onChange={(e) => setFeatureInput(e.target.value)}
                placeholder="Ej: iluminación LED"
                onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddFeature())}
              />
              <Button type="button" variant="outline" onClick={handleAddFeature}>
                <Plus className="w-4 h-4" />
              </Button>
            </div>
            {formData.features?.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {formData.features.map((f, i) => (
                  <span key={i} className="px-2 py-1 bg-secondary rounded-full text-sm flex items-center gap-1">
                    {f}
                    <button type="button" onClick={() => handleRemoveFeature(i)}>
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>

          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={formData.is_active}
              onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
              className="w-4 h-4 rounded"
            />
            <Label className="mb-0">Cancha activa (disponible para reservas)</Label>
          </div>

          <div className="flex gap-3 pt-4">
            <Button type="button" variant="outline" onClick={onClose} className="flex-1">
              Cancelar
            </Button>
            <Button type="submit" className="flex-1" disabled={saving}>
              {saving ? 'Guardando...' : court ? 'Actualizar' : 'Crear Cancha'}
            </Button>
          </div>
        </form>
      </motion.div>
    </motion.div>
  );
};

// QR Code modal
const QRCodeModal = ({ clubId, clubName, onClose }) => {
  const bookingUrl = `${window.location.origin}/reservar/${clubId}`;

  const copyLink = () => {
    navigator.clipboard.writeText(bookingUrl);
    toast.success('Link copiado');
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-background/80 backdrop-blur-sm"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-sm bg-card rounded-2xl border border-border shadow-xl p-6 text-center"
      >
        <h2 className="font-display font-bold text-lg mb-4">Código QR de Reservaciones</h2>
        
        {/* QR Code placeholder - in production use a QR library */}
        <div className="w-48 h-48 mx-auto bg-white rounded-xl flex items-center justify-center mb-4">
          <div className="text-center">
            <QrCode className="w-24 h-24 text-gray-800 mx-auto" />
            <p className="text-xs text-gray-500 mt-2">QR Code</p>
          </div>
        </div>
        
        <p className="text-sm text-muted-foreground mb-4">
          Imprime este QR y colócalo en tu club para que los clientes reserven fácilmente
        </p>
        
        <div className="flex items-center gap-2 p-2 bg-secondary rounded-lg mb-4">
          <input
            type="text"
            value={bookingUrl}
            readOnly
            className="flex-1 bg-transparent text-xs"
          />
          <Button size="sm" variant="ghost" onClick={copyLink}>
            <Copy className="w-4 h-4" />
          </Button>
        </div>
        
        <div className="text-xs text-muted-foreground mb-4">
          También puedes compartir este link por WhatsApp u otros medios
        </div>
        
        <Button onClick={onClose} className="w-full">
          Cerrar
        </Button>
      </motion.div>
    </motion.div>
  );
};

// Reservation details modal
const ReservationModal = ({ reservation, onClose, onStatusChange }) => {
  const [updating, setUpdating] = useState(false);

  const handleStatusChange = async (newStatus) => {
    setUpdating(true);
    try {
      await onStatusChange(reservation.id, newStatus);
      onClose();
    } finally {
      setUpdating(false);
    }
  };

  const statusColors = {
    pending: 'bg-yellow-500/20 text-yellow-400',
    confirmed: 'bg-green-500/20 text-green-400',
    completed: 'bg-blue-500/20 text-blue-400',
    cancelled: 'bg-red-500/20 text-red-400',
    no_show: 'bg-gray-500/20 text-gray-400',
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-background/80 backdrop-blur-sm"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md bg-card rounded-2xl border border-border shadow-xl"
      >
        <div className="p-4 border-b border-border flex items-center justify-between">
          <h2 className="font-display font-bold">Reservación</h2>
          <Button variant="ghost" size="icon" onClick={onClose}>
            <X className="w-5 h-5" />
          </Button>
        </div>

        <div className="p-4 space-y-4">
          {/* Confirmation Code */}
          <div className="text-center p-4 bg-secondary rounded-xl">
            <p className="text-sm text-muted-foreground">Código</p>
            <p className="text-2xl font-display font-bold tracking-wider">{reservation.confirmation_code}</p>
          </div>

          {/* Status */}
          <div className="flex items-center justify-between">
            <span className="text-muted-foreground">Estado</span>
            <span className={`px-3 py-1 rounded-full text-sm ${statusColors[reservation.status]}`}>
              {reservation.status}
            </span>
          </div>

          {/* Details */}
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Cliente</span>
              <span className="font-medium">{reservation.player_name}</span>
            </div>
            {reservation.player_phone && (
              <div className="flex justify-between">
                <span className="text-muted-foreground">Teléfono</span>
                <span>{reservation.player_phone}</span>
              </div>
            )}
            <div className="flex justify-between">
              <span className="text-muted-foreground">Cancha</span>
              <span>{reservation.court_name}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Fecha</span>
              <span>{new Date(reservation.date + 'T12:00:00').toLocaleDateString('es-MX', { 
                weekday: 'long', day: 'numeric', month: 'long' 
              })}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Horario</span>
              <span>{reservation.start_time} - {reservation.end_time}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Precio</span>
              <span className="font-bold text-primary">${reservation.price}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Pago</span>
              <span>{reservation.payment_method === 'at_club' ? 'En el club' : 'En línea'}</span>
            </div>
          </div>

          {reservation.notes && (
            <div className="p-3 bg-secondary rounded-lg">
              <p className="text-xs text-muted-foreground">Notas:</p>
              <p className="text-sm">{reservation.notes}</p>
            </div>
          )}

          {/* Actions */}
          {reservation.status === 'confirmed' && (
            <div className="grid grid-cols-2 gap-2 pt-2">
              <Button
                variant="outline"
                onClick={() => handleStatusChange('completed')}
                disabled={updating}
              >
                <Check className="w-4 h-4 mr-2" />
                Completar
              </Button>
              <Button
                variant="outline"
                className="text-destructive"
                onClick={() => handleStatusChange('no_show')}
                disabled={updating}
              >
                No llegó
              </Button>
            </div>
          )}

          {reservation.status === 'pending' && (
            <div className="grid grid-cols-2 gap-2 pt-2">
              <Button
                onClick={() => handleStatusChange('confirmed')}
                disabled={updating}
              >
                <Check className="w-4 h-4 mr-2" />
                Confirmar
              </Button>
              <Button
                variant="destructive"
                onClick={() => handleStatusChange('cancelled')}
                disabled={updating}
              >
                Cancelar
              </Button>
            </div>
          )}
        </div>
      </motion.div>
    </motion.div>
  );
};

export default function ClubReservations() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [courts, setCourts] = useState([]);
  const [reservations, setReservations] = useState([]);
  const [playtomicReservations, setPlaytomicReservations] = useState([]);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [searchCode, setSearchCode] = useState('');
  
  // Tab state - always 'native' (Playtomic disabled)
  const [activeTab, setActiveTab] = useState('native');
  
  const [showCourtModal, setShowCourtModal] = useState(false);
  const [editingCourt, setEditingCourt] = useState(null);
  const [showQRModal, setShowQRModal] = useState(false);
  const [selectedReservation, setSelectedReservation] = useState(null);
  
  // Handle tab change
  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setSearchParams({ tab });
  };

  useEffect(() => {
    fetchData();
  }, [selectedDate]); // fetchData depends on selectedDate which is in deps

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [courtsRes, reservationsRes] = await Promise.all([
        api.get('/reservations/courts'),
        api.get(`/reservations/bookings?date=${selectedDate}`)
      ]);
      
      setCourts(courtsRes.data.courts || []);
      setReservations(reservationsRes.data.reservations || []);
      
      // Playtomic disabled - set empty array
      setPlaytomicReservations([]);
    } catch (err) {
      console.error('Error fetching data:', err);
      toast.error('Error cargando datos');
    } finally {
      setLoading(false);
    }
  }, [selectedDate]);

  const handleSaveCourt = async (courtData) => {
    try {
      if (editingCourt) {
        await api.patch(`/reservations/courts/${editingCourt.id}`, courtData);
        toast.success('Cancha actualizada');
      } else {
        await api.post('/reservations/courts', courtData);
        toast.success('Cancha creada');
      }
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error guardando cancha');
      throw err;
    }
  };

  const handleDeleteCourt = async (court) => {
    if (!window.confirm(`¿Desactivar "${court.name}"?`)) return;
    
    try {
      await api.delete(`/reservations/courts/${court.id}`);
      toast.success('Cancha desactivada');
      fetchData();
    } catch (err) {
      toast.error('Error desactivando cancha');
    }
  };

  const handleStatusChange = async (reservationId, newStatus) => {
    try {
      await api.patch(`/reservations/bookings/${reservationId}/status?status=${newStatus}`);
      toast.success('Estado actualizado');
      fetchData();
    } catch (err) {
      toast.error('Error actualizando estado');
      throw err;
    }
  };

  const handleCheckIn = async () => {
    if (!searchCode.trim()) return;
    
    try {
      const res = await api.get(`/reservations/bookings/check-in/${searchCode.trim()}`);
      setSelectedReservation(res.data.reservation);
      setSearchCode('');
    } catch (err) {
      toast.error('Código no encontrado');
    }
  };

  // Date navigation
  const changeDate = (days) => {
    const newDate = new Date(selectedDate);
    newDate.setDate(newDate.getDate() + days);
    setSelectedDate(newDate.toISOString().split('T')[0]);
  };

  // Combine native and Playtomic reservations
  const allReservations = [
    ...reservations.map(r => ({ ...r, source: 'native' })),
    ...playtomicReservations
  ];

  // Group reservations by court
  const reservationsByCourt = courts.reduce((acc, court) => {
    // Get native reservations for this court
    const nativeRes = reservations.filter(r => r.court_id === court.id);
    
    // Get Playtomic reservations that match this court (by court number or name)
    const playtomicRes = playtomicReservations.filter(r => 
      r.court_name?.includes(court.name) || 
      r.court_number === parseInt(court.name.match(/\d+/)?.[0] || '0')
    );
    
    acc[court.id] = [
      ...nativeRes.map(r => ({ ...r, source: 'native' })),
      ...playtomicRes
    ].sort((a, b) => a.start_time?.localeCompare(b.start_time || ''));
    
    return acc;
  }, {});

  // Calculate day stats (including Playtomic)
  const dayStats = {
    total: allReservations.length,
    native: reservations.length,
    playtomic: playtomicReservations.length,
    revenue: allReservations.filter(r => ['confirmed', 'completed', 'paid'].includes(r.status))
      .reduce((sum, r) => sum + (r.price || 0), 0),
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-background/80 backdrop-blur-lg border-b border-border">
        <div className="flex items-center justify-between p-4 max-w-6xl mx-auto">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate('/club')}>
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="font-display font-bold text-lg">Reservaciones</h1>
              <p className="text-xs text-muted-foreground">{courts.length} canchas</p>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => setShowQRModal(true)}>
              <QrCode className="w-4 h-4 mr-2" />
              QR
            </Button>
            <Button size="sm" onClick={() => { setEditingCourt(null); setShowCourtModal(true); }}>
              <Plus className="w-4 h-4 mr-2" />
              Cancha
            </Button>
          </div>
        </div>
        
        {/* Tabs - Nativas / Playtomic (mismo tamaño) */}
        <div className="flex items-center gap-2 px-4 pb-3 max-w-6xl mx-auto">
          <button
            onClick={() => handleTabChange('native')}
            className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-colors ${
              activeTab === 'native'
                ? 'bg-primary text-primary-foreground'
                : 'bg-secondary/50 text-muted-foreground hover:bg-secondary'
            }`}
          >
            <Calendar className="w-4 h-4" />
            <span>Nativas</span>
            {reservations.length > 0 && (
              <span className="px-1.5 py-0.5 text-xs rounded-full bg-background/20">
                {reservations.length}
              </span>
            )}
          </button>
          <button
            onClick={() => toast.info('Integración Playtomic - Próximamente')}
            className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-colors bg-secondary/30 text-muted-foreground/60 cursor-not-allowed relative"
            disabled
          >
            <Zap className="w-4 h-4" />
            <span>Playtomic</span>
            <span className="px-1.5 py-0.5 text-xs rounded-full bg-yellow-500/20 text-yellow-500 font-semibold">
              Pronto
            </span>
          </button>
        </div>
      </header>

      <div className="max-w-6xl mx-auto p-4 space-y-6">
        {/* Check-in Search */}
        <div className="glass rounded-xl p-4">
          <div className="flex gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Buscar por código de reservación..."
                value={searchCode}
                onChange={(e) => setSearchCode(e.target.value.toUpperCase())}
                className="pl-10"
                onKeyPress={(e) => e.key === 'Enter' && handleCheckIn()}
              />
            </div>
            <Button onClick={handleCheckIn}>Check-in</Button>
          </div>
        </div>

        {/* Date Navigator */}
        <div className="flex items-center justify-between">
          <Button variant="ghost" size="icon" onClick={() => changeDate(-1)}>
            <ChevronLeft className="w-5 h-5" />
          </Button>
          <div className="text-center">
            <p className="font-display font-bold text-lg">
              {new Date(selectedDate + 'T12:00:00').toLocaleDateString('es-MX', { 
                weekday: 'long', day: 'numeric', month: 'long' 
              })}
            </p>
            <div className="flex items-center justify-center gap-4 text-sm text-muted-foreground">
              <span>{dayStats.total} reservaciones</span>
              <span>${dayStats.revenue.toLocaleString()} estimado</span>
            </div>
          </div>
          <Button variant="ghost" size="icon" onClick={() => changeDate(1)}>
            <ChevronRight className="w-5 h-5" />
          </Button>
        </div>

        {/* Courts List */}
        <div className="space-y-4">
          <h2 className="font-display font-bold text-lg">Mis Canchas</h2>
          
          {courts.length === 0 ? (
            <div className="text-center py-12 glass rounded-xl">
              <Calendar className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
              <p className="text-muted-foreground mb-4">No tienes canchas registradas</p>
              <Button onClick={() => { setEditingCourt(null); setShowCourtModal(true); }}>
                <Plus className="w-4 h-4 mr-2" />
                Crear primera cancha
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              {courts.map((court) => {
                const courtReservations = reservationsByCourt[court.id] || [];
                
                return (
                  <div key={court.id} className={`glass rounded-xl overflow-hidden ${!court.is_active ? 'opacity-50' : ''}`}>
                    {/* Court Header */}
                    <div className="p-4 border-b border-border flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="flex items-center gap-2">
                          <h3 className="font-medium">{court.name}</h3>
                          {court.has_hiksports && (
                            <span className="px-2 py-0.5 rounded-full text-xs bg-primary/20 text-primary flex items-center gap-1">
                              <Play className="w-3 h-3" /> Replay
                            </span>
                          )}
                        </div>
                        <span className="text-sm text-muted-foreground">${court.price_per_hour}/hr</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          onClick={() => { setEditingCourt(court); setShowCourtModal(true); }}
                        >
                          <Edit2 className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-destructive"
                          onClick={() => handleDeleteCourt(court)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                    
                    {/* Reservations */}
                    {courtReservations.length === 0 ? (
                      <div className="p-4 text-center text-sm text-muted-foreground">
                        Sin reservaciones para este día
                      </div>
                    ) : (
                      <div className="divide-y divide-border">
                        {courtReservations.map((r) => (
                          <button
                            key={r.id}
                            onClick={() => r.source !== 'playtomic' && setSelectedReservation(r)}
                            className={`w-full p-3 flex items-center justify-between hover:bg-secondary/50 transition-colors text-left ${
                              r.source === 'playtomic' ? 'bg-blue-500/5 border-l-2 border-l-blue-500' : ''
                            }`}
                          >
                            <div className="flex items-center gap-3">
                              <div className="text-center min-w-[50px]">
                                <p className="font-medium">{r.start_time}</p>
                                <p className="text-xs text-muted-foreground">{r.end_time}</p>
                              </div>
                              <div>
                                <div className="flex items-center gap-2">
                                  <p className="font-medium">{r.player_name}</p>
                                  {r.source === 'playtomic' && (
                                    <span className="px-1.5 py-0.5 rounded text-[10px] bg-blue-500/20 text-blue-400 font-medium">
                                      Playtomic
                                    </span>
                                  )}
                                </div>
                                <p className="text-xs text-muted-foreground">
                                  {r.confirmation_code || r.id?.slice(-6)?.toUpperCase()} • {
                                    r.payment_method === 'at_club' ? 'Paga al llegar' : 
                                    r.payment_status === 'paid_online' || r.payment_status === 'paid_at_club' ? 'Pagado' :
                                    r.payment_status === 'pending' ? 'Por cobrar' : 'Pagado'
                                  }
                                </p>
                              </div>
                            </div>
                            <div className="text-right">
                              <p className="font-bold">${r.price?.toLocaleString()}</p>
                              <span className={`text-xs px-2 py-0.5 rounded-full ${
                                r.status === 'confirmed' ? 'bg-green-500/20 text-green-400' :
                                r.status === 'pending' ? 'bg-yellow-500/20 text-yellow-400' :
                                r.status === 'completed' || r.status === 'paid' ? 'bg-blue-500/20 text-blue-400' :
                                r.status === 'cancelled' ? 'bg-red-500/20 text-red-400' :
                                'bg-gray-500/20 text-gray-400'
                              }`}>
                                {r.status === 'paid' ? 'pagado' : r.status}
                              </span>
                            </div>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Modals */}
      <AnimatePresence>
        {showCourtModal && (
          <CourtModal
            court={editingCourt}
            onClose={() => { setShowCourtModal(false); setEditingCourt(null); }}
            onSave={handleSaveCourt}
          />
        )}
        {showQRModal && (
          <QRCodeModal
            clubId="8d51dce4-7b5d-4302-94e0-e130dec72390"
            clubName="Paddra Padel"
            onClose={() => setShowQRModal(false)}
          />
        )}
        {selectedReservation && (
          <ReservationModal
            reservation={selectedReservation}
            onClose={() => setSelectedReservation(null)}
            onStatusChange={handleStatusChange}
          />
        )}
      </AnimatePresence>
    </div>
  );
}
